import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityLayout } from '@/components/layout/EntityLayout';
import { UserJourney } from '@/components/shared/UserJourney';
import { Button } from '@/components/shared/Button';
import { ParameterForm } from '@/components/entity-modules/ParameterForm';
import { PrototypeGallery } from '@/components/entity-modules/PrototypeGallery';
import { Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { ENTITY_CONFIGS } from '@/lib/entity-config';
import { useUser } from '@/contexts/UserContext';
import { useQueueListener } from '@/hooks/useQueueListener';
import type { SidebarSection } from '@/components/layout/EntityLayout';

const API_BASE_URL = 'https://fdaia-api.lancetkeith.workers.dev';

interface GeneratedVariant {
  av_id: string;
  image_url: string;
  cloudflare_image_id: string;
  prompt_used: string;
  llm_provider: string;
  generation_time_ms: number;
  buy_off_status: string;
}

interface Prototype {
  op_id: string;
  name: string;
  category: string;
  style_taxonomy: string;
  image_url: string;
}

export function FDSCanvas() {
  const entity = ENTITY_CONFIGS.fdaia;
  const navigate = useNavigate();
  const { userId, userTier, setUserTier } = useUser();

  // Queue listener for real-time variant generation updates
  // TODO: Enable after Cloudflare Queues WebSocket configured
  const { isConnected: queueConnected } = useQueueListener(
    'design-generation',
    { enabled: false } // Disabled until WebSocket endpoint ready
  );

  const [selectedPrototype, setSelectedPrototype] = useState<Prototype | null>(null);
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVariant, setGeneratedVariant] = useState<GeneratedVariant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSave = async () => {
    if (!generatedVariant) {
      console.warn('handleSave called but no variant generated');
      return;
    }

    console.log('Saving variant to AL3Viewer:', generatedVariant.av_id);
    setError(null);

    try {
      // Register with AL3Viewer
      console.log('Calling AL3Viewer register API...');
      const response = await fetch('https://al3viewer-api.lancetkeith.workers.dev/api/al3viewer/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variant_id: generatedVariant.av_id,
          prototype_id: selectedPrototype?.op_id || 'op-001',
          asset_type: 'variant',
          owner_user_id: userId || 'user-temp-id', // From auth context
          asset_name: selectedPrototype?.name || 'Custom Variant',
          image_url: generatedVariant.image_url,
          closet_category: 'created'
        })
      });

      console.log('AL3Viewer API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('AL3Viewer API error response:', errorText);
        throw new Error(`AL3Viewer API returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('AL3Viewer API response data:', data);

      if (data.success && data.asset_id) {
        console.log('Navigating to AL3Viewer with asset_id:', data.asset_id);
        // Navigate to AL3Viewer with asset data
        navigate('/al3viewer', {
          state: {
            asset_id: data.asset_id,
            variant: generatedVariant
          }
        });
      } else {
        throw new Error(`AL3Viewer registration failed: ${JSON.stringify(data)}`);
      }
    } catch (err) {
      console.error('Error saving to AL3Viewer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to save variant: ${errorMessage}`);
      setShowSuccess(false);
    }
  };

  const handleParametersChange = (params: Record<string, any>) => {
    setParameters(params);
    setError(null);
  };

  const handleGenerate = async () => {
    if (!selectedPrototype) {
      setError('Please select a prototype first');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);
      setShowSuccess(false);

      const response = await fetch(`${API_BASE_URL}/api/fdaia/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          original_prototype_id: selectedPrototype.op_id,
          user_inputs: parameters,
          user_tier: userTier,
          variant_count: 1,
        }),
      });

      if (!response.ok) {
        throw new Error(`Generation failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.variants && data.variants.length > 0) {
        setGeneratedVariant(data.variants[0]);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        throw new Error('No variants generated');
      }
    } catch (err) {
      console.error('Error generating variant:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsGenerating(false);
    }
  };

  const headerActions = (
    <div className="flex items-center gap-3">
      {/* Development Tier Switcher - TODO: Remove after Better Auth API deployed */}
      <select
        value={userTier}
        onChange={(e) => setUserTier(e.target.value as 'free' | 'basic' | 'pro' | 'designer')}
        className="rounded-lg border border-ck-dark-border bg-ck-dark-card px-3 py-2 text-sm text-ck-cyan font-medium focus:border-ck-cyan focus:outline-none focus:ring-1 focus:ring-ck-cyan uppercase"
        title="Development tier switcher (will use session tier after auth deployed)"
      >
        <option value="free">FREE Tier</option>
        <option value="basic">BASIC Tier</option>
        <option value="pro">PRO Tier</option>
        <option value="designer">DESIGNER Tier</option>
      </select>

      {/* Queue Status Indicator - TODO: Enable after WebSocket configured */}
      {queueConnected && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-green-500/30 bg-green-500/10">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs text-green-400 font-medium">Live Updates</span>
        </div>
      )}

      <Button variant="gradient" size="lg" onClick={handleSave} disabled={!generatedVariant}>
        <Save className="h-4 w-4" />
        Save to AL3Viewer
      </Button>
    </div>
  );

  const sidebarSections: SidebarSection[] = [
    {
      id: 'prototypes',
      title: 'Select Prototype',
      items: [],
      content: (
        <PrototypeGallery
          onSelect={setSelectedPrototype}
          selectedPrototypeId={selectedPrototype?.op_id}
        />
      )
    },
    {
      id: 'parameters',
      title: 'Design Parameters',
      items: [],
      content: (
        <ParameterForm
          tier={userTier}
          onParametersChange={handleParametersChange}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
        />
      )
    }
  ];

  return (
    <>
      <UserJourney currentStep="fdaia" sessionId="2024-0847" />

      {showSuccess && (
        <div className="border-b border-green-500/20 bg-green-500/10 px-4 py-3">
          <div className="flex items-center justify-center space-x-2 text-green-400">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Variant generated successfully!</span>
          </div>
        </div>
      )}

      {error && (
        <div className="border-b border-red-500/20 bg-red-500/10 px-4 py-3">
          <div className="flex items-center justify-center space-x-2 text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        </div>
      )}

      <EntityLayout
        entity={entity}
        headerActions={headerActions}
        sidebarSections={sidebarSections}
        aiTitle="AI Design Assistant"
        aiInitialMessage="Welcome! I'm here to help you design the perfect footwear. What kind of shoe are you envisioning today?"
        aiPlaceholder="Ask AI for design suggestions..."
        onClose={() => navigate('/')}
      >
        <div className="min-h-full bg-gradient-to-br from-ck-dark via-ck-dark-card to-ck-dark p-4 md:p-8 lg:p-12">
          <div className="flex flex-col items-center space-y-4 md:space-y-6">
            {/* Selected Prototype Preview */}
            {selectedPrototype && !generatedVariant && !isGenerating && (
              <div className="w-full max-w-[700px] mb-4">
                <div className="rounded-lg border border-ck-purple/30 bg-ck-dark-card/50 p-4">
                  <div className="flex items-center gap-4">
                    <img
                      src={selectedPrototype.image_url}
                      alt={selectedPrototype.name}
                      className="h-24 w-24 rounded-lg object-cover"
                    />
                    <div>
                      <p className="text-sm text-ck-purple font-medium">Selected Prototype</p>
                      <p className="text-lg font-bold text-foreground">{selectedPrototype.name}</p>
                      <p className="text-xs text-muted-foreground uppercase">
                        {selectedPrototype.category} · {selectedPrototype.style_taxonomy.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Generated Variant Display */}
            <div className="w-full max-w-[700px]">
              {isGenerating ? (
                <div className="flex h-[400px] items-center justify-center rounded-lg border-2 border-dashed border-ck-cyan/30 bg-ck-dark-card/50 md:h-[500px] lg:h-[600px]">
                  <div className="text-center">
                    <Loader2 className="mx-auto h-12 w-12 animate-spin text-ck-cyan" />
                    <p className="mt-4 text-lg font-medium text-muted-foreground">Generating variant...</p>
                    <p className="mt-2 text-sm text-muted-foreground">This may take 5-10 seconds</p>
                  </div>
                </div>
              ) : generatedVariant ? (
                <div className="space-y-4">
                  <div className="overflow-hidden rounded-lg border-2 border-ck-cyan/50 bg-ck-dark-card/50">
                    <img
                      src={generatedVariant.image_url}
                      alt="Generated variant"
                      className="h-auto w-full"
                    />
                  </div>
                  <div className="rounded-lg border border-ck-dark-border bg-ck-dark-card p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Status:</span>
                        <span className="ml-2 font-medium text-ck-cyan">{generatedVariant.buy_off_status}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Generation Time:</span>
                        <span className="ml-2 font-medium text-ck-cyan">
                          {(generatedVariant.generation_time_ms / 1000).toFixed(1)}s
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Provider:</span>
                        <span className="ml-2 font-medium text-ck-cyan">{generatedVariant.llm_provider}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">ID:</span>
                        <span className="ml-2 font-mono text-xs text-ck-cyan">{generatedVariant.av_id}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex h-[400px] items-center justify-center rounded-lg border-2 border-dashed border-ck-cyan/30 bg-ck-dark-card/50 md:h-[500px] lg:h-[600px]">
                  <div className="text-center">
                    <p className="text-lg font-medium text-muted-foreground">Your Design Canvas</p>
                    <p className="mt-2 text-sm text-muted-foreground">Select parameters and generate to see your variant</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Parameter Form */}
        <div className="max-h-[50vh] overflow-y-auto border-t border-ck-dark-border bg-black p-4 lg:hidden">
          <ParameterForm
            tier={userTier}
            onParametersChange={handleParametersChange}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
          />
        </div>
      </EntityLayout>
    </>
  );
}